import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Shared/Sidebar';
import Header from '../Shared/Header';
import { API_BASE_URL } from '../../config';
import { useTranslation } from 'react-i18next';
import './ModelPaperGenerator.css';

const ModelPaperGenerator = () => {
    const { t } = useTranslation(['model_paper', 'common', 'navigation']);
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        boardId: localStorage.getItem('boardId') || '',
        classId: '',
        subjectId: '',
        chapterId: '',
        difficulty: 'Medium',
        numQuestions: 10
    });

    const [boardName, setBoardName] = useState(localStorage.getItem('boardName') || '');
    const [classes, setClasses] = useState([]);
    const [subjects, setSubjects] = useState([]);
    const [chapters, setChapters] = useState([]);
    const [credits, setCredits] = useState({ total: 0, remaining: 0 });
    const [showBuyModal, setShowBuyModal] = useState(false);
    const [cashfree, setCashfree] = useState(null);

    const [loading, setLoading] = useState({
        boards: false,
        classes: false,
        subjects: false,
        chapters: false,
        generating: false,
        credits: false,
        topup: false
    });

    const userId = localStorage.getItem('userId');

    // Initialize Cashfree
    useEffect(() => {
        if (window.Cashfree) {
            setCashfree(window.Cashfree({ mode: "production" })); // Use "production" for live
        }
    }, []);

    // Fetch credits
    const fetchCredits = useCallback(async () => {
        setLoading(prev => ({ ...prev, credits: true }));
        try {
            const res = await fetch(`${API_BASE_URL}get_usage_count.php?user_id=${userId}`);
            const data = await res.json();
            if (data.status === 'success') {
                setCredits({ total: data.total_count, remaining: data.remaining_count });
            }
        } catch (err) {
            console.error("Error fetching credits:", err);
        } finally {
            setLoading(prev => ({ ...prev, credits: false }));
        }
    }, [userId]);

    const verifyPayment = async (orderId) => {
        try {
            const res = await fetch(`${API_BASE_URL}verify_cashfree_payment.php?order_id=${orderId}`);
            const data = await res.json();
            if (data.status === 'success' && data.order_status === 'SUCCESS') {
                alert(t('model_paper:model_paper.alerts.paymentSuccess', 'Payment Successful! 5 credits added to your account.'));
                fetchCredits();
                setShowBuyModal(false);
            } else {
                alert(t('model_paper:model_paper.alerts.paymentFailed', { status: data.order_status || "FAILED", defaultValue: "Payment was not successful." }));
            }
        } catch (err) {
            console.error("Verification error:", err);
        }
    };

    const handleTopUp = async () => {
        if (!cashfree) {
            alert(t('model_paper:model_paper.alerts.gatewayError', 'Payment gateway not initialized. Please refresh.'));
            return;
        }

        setLoading(prev => ({ ...prev, topup: true }));
        try {
            // 1. Create Real Order
            const res = await fetch(`${API_BASE_URL}create_cashfree_order.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ 
                    user_id: userId,
                    amount: 9,
                    plan_name: 'Model Paper Package'
                })
            });
            const data = await res.json();
            
            if (data.status === 'success' && data.payment_session_id) {
                // 2. Launch Cashfree Checkout
                let checkoutOptions = {
                    paymentSessionId: data.payment_session_id,
                    redirectTarget: "_modal", // Opens in a nice modal
                };

                cashfree.checkout(checkoutOptions).then((result) => {
                    // 3. Verify on Close/Success
                    console.log("Checkout closed/finished");
                    verifyPayment(data.order_id);
                });
            } else {
                alert(t('model_paper:model_paper.alerts.initFailed', 'Payment initialization failed. Please try again.'));
            }
        } catch (err) {
            console.error("Top-up error:", err);
            alert(t('model_paper:model_paper.alerts.initFailed', 'Payment initialization failed. Please try again.'));
        } finally {
            setLoading(prev => ({ ...prev, topup: false }));
        }
    };
// Fetch Boards and Credits on Mount
    useEffect(() => {
        fetchCredits();
        const fetchBoards = async () => {
            setLoading(prev => ({ ...prev, boards: true }));
            try {
                const res = await fetch(`${API_BASE_URL}get_boards.php`);
                const data = await res.json();
                // const boardList = Array.isArray(data) ? data : [];
                // setBoards(boardList);
                const boardList = Array.isArray(data) ? data : [];

                // If boardName is missing, find it in the list
                if (!boardName && formData.boardId && boardList.length > 0) {
                    const match = boardList.find(b => String(b.id) === String(formData.boardId));
                    if (match) {
                        setBoardName(match.name);
                        localStorage.setItem('boardName', match.name);
                    }
                }
            } catch (err) {
                console.error("Error fetching boards:", err);
            } finally {
                setLoading(prev => ({ ...prev, boards: false }));
            }
        };
        fetchBoards();
    }, [formData.boardId, boardName, fetchCredits]);

    // Fetch Classes when Board changes
    useEffect(() => {
        if (!formData.boardId) {
            setClasses([]);
            return;
        }
        const fetchClasses = async () => {
            setLoading(prev => ({ ...prev, classes: true }));
            try {
                const res = await fetch(`${API_BASE_URL}get_classes.php?board_id=${formData.boardId}`);
                const data = await res.json();
                setClasses(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching classes:", err);
            } finally {
                setLoading(prev => ({ ...prev, classes: false }));
            }
        };
        fetchClasses();
    }, [formData.boardId]);

    // Fetch Subjects when Class changes
    useEffect(() => {
        if (!formData.classId) {
            setSubjects([]);
            return;
        }
        const fetchSubjects = async () => {
            setLoading(prev => ({ ...prev, subjects: true }));
            try {
                const res = await fetch(`${API_BASE_URL}get_subjects.php?class_id=${formData.classId}`);
                const data = await res.json();
                setSubjects(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching subjects:", err);
            } finally {
                setLoading(prev => ({ ...prev, subjects: false }));
            }
        };
        fetchSubjects();
    }, [formData.classId]);

    // Fetch Chapters when Subject changes
    useEffect(() => {
        if (!formData.subjectId) {
            setChapters([]);
            return;
        }
        const fetchChapters = async () => {
            setLoading(prev => ({ ...prev, chapters: true }));
            try {
                const res = await fetch(`${API_BASE_URL}get_chapters.php?subject_id=${formData.subjectId}`);
                const data = await res.json();
                setChapters(Array.isArray(data) ? data : []);
            } catch (err) {
                console.error("Error fetching chapters:", err);
            } finally {
                setLoading(prev => ({ ...prev, chapters: false }));
            }
        };
        fetchChapters();
    }, [formData.subjectId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        
        // Reset dependent fields
        if (name === 'boardId') {
            setFormData(prev => ({ ...prev, boardId: value, classId: '', subjectId: '', chapterId: '' }));
        } else if (name === 'classId') {
            setFormData(prev => ({ ...prev, classId: value, subjectId: '', chapterId: '' }));
        } else if (name === 'subjectId') {
            setFormData(prev => ({ ...prev, subjectId: value, chapterId: '' }));
        } else {
            setFormData(prev => ({
                ...prev,
                [name]: value
            }));
        }
    };


    const handleSubmit = async (e) => {
        e.preventDefault();

        // Paywall logic: if remaining count is 0
        if (credits.remaining <= 0) {
            setShowBuyModal(true);
            return;
        }

        setLoading(prev => ({ ...prev, generating: true }));

        try {
            const selectedClass = classes.find(c => String(c.id) === String(formData.classId))?.class_name || formData.classId;
            const selectedSubject = subjects.find(s => String(s.id) === String(formData.subjectId))?.subject_name || formData.subjectId;
            const selectedChapter = chapters.find(ch => String(ch.id) === String(formData.chapterId))?.chapter_name || t('model_paper:model_paper.form.entireSubject', 'Entire Subject');

            const payload = {
                user_id: userId,
                board: boardName,
                class: selectedClass,
                subject: selectedSubject,
                num_questions: formData.numQuestions,
                difficulty: formData.difficulty
            };

            const response = await fetch(`${API_BASE_URL}generate_model_paper.php`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            const result = await response.json();

            if (result.questions) {
                // Refresh credits after successful generation
                fetchCredits();
                
                navigate('/view-paper', { 
                    state: { 
                        questions: result.questions, 
                        info: {
                            ...payload,
                            chapter: selectedChapter
                        } 
                    } 
                });
            } else if (result.insufficient_credits) {
                setShowBuyModal(true);
            } else {
                alert(result.message || t('model_paper:model_paper.alerts.connError', 'Connection error. Please try again.'));
            }
        } catch (error) {
            console.error("Error generating paper:", error);
            alert(t('model_paper:model_paper.alerts.connError', 'Connection error. Please try again.'));
        } finally {
            setLoading(prev => ({ ...prev, generating: false }));
        }
    };

    return (
        <div className="dashboard-wrapper">
            <div className="dashboard-container">
                <Sidebar active="model_paper" />

                <main className="main-content-area">
                    <Header showActions={true} hidePill={true} />
                    
                    {loading.generating && (
                        <div className="ai-loading-overlay">
                            <div className="ai-loader-container">
                                <div className="glowing-ai-sphere">
                                    <img src="/ai_diagnostic_icon.png" alt="AI Diagnostic" className="ai-diagnostic-img" />
                                </div>
                                <div className="ai-rings">
                                    <div className="ring"></div>
                                    <div className="ring"></div>
                                    <div className="ring"></div>
                                </div>
                                <div className="ai-loading-text">
                                    <h3>{t('model_paper:model_paper.thinking', 'SkillTherapy AI is Thinking...')}</h3>
                                    <div className="ai-status-wrapper">
                                        <p className="ai-step">{t('model_paper:model_paper.generatingStep', 'Analyzing syllabus and crafting questions...')}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    <div className="dashboard-scroll-container">
                        <div className="model-paper-content">
                            <div className="paper-card">
                                <div className="card-title">
                                    <div className="title-left">
                                        <img src="/favicon.png" alt="AI Icon" className="ai-icon" />
                                        <h2>{t('model_paper:model_paper.title', 'AI Model Paper Generator')}</h2>
                                    </div>
                                    <div className="credits-badge" title={t('model_paper:model_paper.remainingCredits', 'Remaining AI Credits')}>
                                        <span className="icon">⚡</span>
                                        <span className="count">
                                            {loading.credits ? '...' : `${credits.remaining} / ${credits.total}`} {t('model_paper:model_paper.credits', 'Credits')}
                                        </span>
                                    </div>
                                </div>

                                <form className="paper-form" onSubmit={handleSubmit}>
                                    <div className="form-group">
                                        <label>{t('model_paper:model_paper.form.board', 'Board')}</label>
                                        <select
                                            name="boardId"
                                            value={formData.boardId}
                                            onChange={handleChange}
                                            required
                                            disabled={true}
                                        >
                                            <option value={formData.boardId}>{boardName || t('model_paper:model_paper.form.yourBoard', 'Your Board')}</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>{t('model_paper:model_paper.form.class', 'Class')}</label>
                                        <select
                                            name="classId"
                                            value={formData.classId}
                                            onChange={handleChange}
                                            required
                                            disabled={!formData.boardId || loading.classes}
                                        >
                                            <option value="">{loading.classes ? t('model_paper:model_paper.form.loading', 'Loading...') : t('model_paper:model_paper.form.selectClass', 'Select Class')}</option>
                                            {classes.map(c => (
                                                <option key={c.id} value={c.id}>{c.class_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>{t('model_paper:model_paper.form.subject', 'Subject')}</label>
                                        <select
                                            name="subjectId"
                                            value={formData.subjectId}
                                            onChange={handleChange}
                                            required
                                            disabled={!formData.classId || loading.subjects}
                                        >
                                            <option value="">{loading.subjects ? t('model_paper:model_paper.form.loading', 'Loading...') : t('model_paper:model_paper.form.selectSubject', 'Select Subject')}</option>
                                            {subjects.map(s => (
                                                <option key={s.id} value={s.id}>{s.subject_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>{t('model_paper:model_paper.form.chapter', 'Chapter')}</label>
                                        <select
                                            name="chapterId"
                                            value={formData.chapterId}
                                            onChange={handleChange}
                                            disabled={!formData.subjectId || loading.chapters}
                                        >
                                            <option value="">{loading.chapters ? t('model_paper:model_paper.form.loading', 'Loading...') : t('model_paper:model_paper.form.selectChapter', 'Select Chapter (Entire Subject if empty)')}</option>
                                            {chapters.map(ch => (
                                                <option key={ch.id} value={ch.id}>{ch.chapter_name}</option>
                                            ))}
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>{t('model_paper:model_paper.form.difficulty', 'Difficulty')}</label>
                                        <select
                                            name="difficulty"
                                            value={formData.difficulty}
                                            onChange={handleChange}
                                        >
                                            <option value="Easy">{t('model_paper:model_paper.form.difficulties.easy', 'Easy')}</option>
                                            <option value="Medium">{t('model_paper:model_paper.form.difficulties.medium', 'Medium')}</option>
                                            <option value="Hard">{t('model_paper:model_paper.form.difficulties.hard', 'Hard')}</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label>{t('model_paper:model_paper.form.numQuestions', 'Number of Questions')}</label>
                                        <input
                                            type="number"
                                            name="numQuestions"
                                            min="1"
                                            max="100"
                                            value={formData.numQuestions}
                                            onChange={handleChange}
                                        />
                                    </div>

                                    <button 
                                        type="submit" 
                                        className="generate-btn"
                                        disabled={loading.generating}
                                    >
                                        {loading.generating ? t('model_paper:model_paper.generatingBtn', 'Generating Paper...') : t('model_paper:model_paper.generateBtn', 'Generate Paper')}
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </main>
            </div>
            {showBuyModal && (
                <div className="buy-modal-overlay">
                    <div className="buy-modal-card">
                        <button className="close-modal" onClick={() => setShowBuyModal(false)}>×</button>
                        <div className="modal-icon">💎</div>
                        <h2>{t('model_paper:model_paper.buyModal.title', 'Unlock More AI Papers')}</h2>
                        <p>{t('model_paper:model_paper.buyModal.description', "You've used all your free credits. Get 5 more high-quality AI model papers for just Rs. 9!")}</p>
                        
                        <div className="benefit-list">
                            <div className="benefit-item">✓ {t('model_paper:model_paper.buyModal.benefits.0', '5 Professional Model Papers')}</div>
                            <div className="benefit-item">✓ {t('model_paper:model_paper.buyModal.benefits.1', 'AI-Powered Difficulty Balance')}</div>
                            <div className="benefit-item">✓ {t('model_paper:model_paper.buyModal.benefits.2', 'Full Sectioned Layout')}</div>
                        </div>

                        <button 
                            className="pay-now-btn" 
                            onClick={handleTopUp}
                            disabled={loading.topup}
                        >
                            {loading.topup ? t('model_paper:model_paper.buyModal.processing', 'Processing...') : t('model_paper:model_paper.buyModal.payBtn', 'Pay Rs. 9 Now')}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ModelPaperGenerator;
